<script lang="ts" setup>
import {FILE_STATUS} from '../../media/utils/enums/FileStatus';

import useSnackbarStore from '#core/store/snackbar';
import type {PresignChunk} from '#core/types/PresignChunk';
import {filter, find, includes} from 'lodash';
import DropZone from './DropZone.vue';
import {AppButton, AppTypography} from '~/components/base'

const {
  loading = false,
  multiple = false,
  fileTypes = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  errorMessages = '',
  alwaysShowDropzone = true,
  autoUpload = false,
  dragDrop = true,
} = defineProps<{
  loading?: boolean;
  multiple?: boolean;
  fileTypes?: string[];
  errorMessages?: any;
  alwaysShowDropzone?: boolean;
  autoUpload?: boolean;
  dragDrop?: boolean;
}>();

const {showSnackbar} = useSnackbarStore();
const media = defineModel<number[] | File[] | FileList>();

const errorMessage = computed(() => {
  if (Array.isArray(errorMessages)) {
    return errorMessages.join(', ');
  }
  return errorMessages;
});

const readableTypes = computed(() => {
  const types = fileTypes.map((type) => type.split('/')[1]);

  // replace vnd.openxmlformats-officedocument.wordprocessingml.document to docx
  types[types.indexOf('vnd.openxmlformats-officedocument.wordprocessingml.document')] = 'docx';

  return types.join(', ').toUpperCase();
});

const {files, removeFile, clearFiles, addFiles} = useFileList();

defineExpose({
  clearFiles,
});

const mediaIds = ref<number[]>([]);
const uploadInProgress = ref(false);

const handleUpload = async (file: File, retryChunk: PresignChunk = null) => {
  uploadInProgress.value = true;

  const setFileStatus = (status: string, presign: PresignChunk = null) => {
    const existingFile = find(files.value, {_file: file});
    if (existingFile) {
      existingFile.status = status;
      existingFile.presign = presign;
    }
  };

  try {
    const resp = await useMediaUploader(file, retryChunk);
    mediaIds.value.push(resp);

    setFileStatus(FILE_STATUS.success);
  } catch (e) {
    setFileStatus(FILE_STATUS.fail, e);
    showSnackbar({text: `Fail to upload file ${file.name}`.trim(), state: false});
  }

  uploadInProgress.value = false;
};

const uploadMedia = async (uploadingFiles: File[]) => {
  for (const file of uploadingFiles) {
    await handleUpload(file);
  }

  media.value = mediaIds.value;
};

const retryUpload = async (file: File) => {
  await handleUpload(file.file, file.presign);

  media.value = mediaIds.value;
};

const addSelectedFiles = (file: File[]) => {
  // check if valid file types using lodash
  const validFiles = filter(file, (file) => {
    return includes(fileTypes, file.type);
  });

  // check if file are empty
  if (validFiles.length === 0 && file.length > 0) {
    return;
  }

  if (multiple) {
    addFiles(file);
  } else {
    clearFiles();
    addFiles(file);
  }

  if (autoUpload) {
    uploadMedia(file);
  } else {
    media.value = files.value.map((file) => file.file);
  }
};

const removeSelectedFile = (file: File | number) => {
  if (multiple) {
    removeFile(file);
  } else {
    clearFiles();
  }

  media.value = files.value.map((file) => file.file);

  // TODO: call to delete uploaded pre-signed media
};

const showDropzone = computed(() => {
  return files.value.length === 0 || alwaysShowDropzone;
});

onMounted(() => {
  if (media.value) {
    if (Array.isArray(media.value)) {
      files.value = media.value;
    } else {
      files.value.push(media.value);
    }
  }
});
</script>

<template>
  <div>
    <input
        ref="fileInput"
        type="file"
        :multiple="multiple"
        :accept="fileTypes"
        style="display: none"
        @change="addSelectedFiles($event.target.files)"
    />
    <v-card v-if="showDropzone" class="mx-auto" variant="flat" @click="$refs.fileInput.click()">
      <DropZone v-slot="{ dropZoneActive }" class="drop-area" @files-dropped="addSelectedFiles" v-if="dragDrop">
        <v-row class="d-flex justify-center align-center" :class="dropZoneActive ? 'drop-zone-active' : ''">
          <v-col cols="12" class="text-center">
            <v-btn class="tray-arrow-up-btn mb-4" variant="plain">
              <v-icon size="25">mdi-tray-arrow-up</v-icon>
            </v-btn>
            <div class="mb-2">
              <span class="mt-3 font-weight-bold">Drag and Drop or</span>
              <NuxtLink class="ml-1 font-weight-bold choose-file-link"> Choose File</NuxtLink>
            </div>
            <div>Supported File Types ({{ readableTypes }})</div>
          </v-col>
        </v-row>
      </DropZone>
    </v-card>

    <div v-if="!dragDrop && showDropzone">
      <AppTypography element="span" type="semibold" class="mb-3 d-flex w-100">Attach a File</AppTypography>
      <AppButton
          color=""
          variant="plain"
          class="d-flex border-thin"
          size="small"
          @click="$refs.fileInput.click()"
          type="button"
      >
        Upload
      </AppButton>
    </div>
    <!-- list of files -->
    <v-progress-linear v-if="loading || uploadInProgress" indeterminate color="primary" class="mt-2"/>
    <div v-for="file in files" :key="file.name" class="file-list mt-2 pr-1">
      <v-row no-gutters class="file-list__file">
        <v-col cols="11" class="pl-2 py-2 d-flex align-center">
          <span class="file-list__file__name text-truncate">{{ file.name }}</span>
        </v-col>
        <v-col cols="1" class="text-right py-2">
          <div class="d-flex align-center justify-space-between">
            <v-btn
                v-if="file.status === FILE_STATUS.fail"
                variant="text"
                color="error"
                class="file-list__file__retry mr-3"
                :disabled="loading || uploadInProgress"
                @click="retryUpload(file)"
            >
              <p class="font-subheadline-1">Retry?</p>
            </v-btn>
            <v-btn
                variant="text"
                icon="mdi-trash-can-outline"
                class="file-list__file__remove mr-3"
                :disabled="loading || uploadInProgress"
                @click="removeSelectedFile(file)"
            />
          </div>
        </v-col>
      </v-row>
    </div>
    <div v-if="errorMessages?.length > 0 || errorMessages !== ''" class="v-messages pl-4 pt-2" aria-live="polite">
      <div class="v-messages__message">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.file-list__file__name{
  max-width: 400px !important;
  height: 24px !important;
}

.drop-area {
  border: 2px dashed #c0c0c0;
  border-radius: 5px;
  padding: 1rem;
  text-align: center;
  cursor: pointer;
}

.drop-zone-active {
  border: 1px dashed #000;
}

.tray-arrow-up-btn {
  height: 50px;
  width: 50px;
}

.choose-file-link {
  color: black;
  cursor: pointer;
  text-decoration: underline;
}

.v-messages__message {
  color: red;
}

.file-list {
  background-color: #f5f5f5;
  border-radius: 8px;
}
</style>
